import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageSquare, Eye, Key } from 'lucide-react';
import SpecialismSection from '@/components/SpecialismSection';
import ShopfrontBanner from '@/components/ShopfrontBanner';
import ComparisonSection from '@/components/ComparisonSection';
import PractitionerBanner from '@/components/PractitionerBanner';
import SEO from '@/components/SEO';

const HERO_IMG = 'https://images.unsplash.com/photo-1719760518176-e124a5bcd025?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxtaW5pbWFsJTIwd2VsbG5lc3MlMjBzdHVkaW8lMjBpbnRlcmlvciUyMG5ldXRyYWwlMjB0b25lc3xlbnwwfHx8fDE3NzU0NzM1OTB8MA&ixlib=rb-4.1.0&q=85';

const steps = [
  { num: '1', title: 'Enquire', icon: MessageSquare },
  { num: '2', title: 'View & decide', icon: Eye },
  { num: '3', title: 'Move in and focus on your clients', icon: Key },
];

export default function HomePage() {
  return (
    <div data-testid="home-page">
      <SEO path="/" />

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-20 sm:py-28 lg:py-36">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="animate-fade-in">
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-[#1A1A1A] tracking-tight leading-none mb-6">
                Your own studio.<br />Space to grow.
              </h1>
              <p className="text-base sm:text-lg text-[#5C5C55] leading-relaxed mb-6 max-w-lg">
                Private studios for health, wellbeing and beauty practitioners. A professional space to welcome your clients, build your business and make your next move.
              </p>
              <p className="text-sm text-[#5C5C55]/80 mb-4">View a studio. See how it works. Decide with confidence.</p>
              <div className="flex flex-wrap gap-3">
                <Link to="/book-a-viewing" className="bg-[#3A4035] text-white rounded-full px-7 py-3.5 text-sm font-medium hover:bg-[#2B3027] transition-colors flex items-center gap-2" data-testid="hero-cta-viewing">
                  Book a viewing <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </Link>
              </div>
              <p className="text-sm text-[#8A8B85] mt-4">No pressure. No obligation. Just come and see.</p>
              <p className="text-sm text-[#5C5C55]/70 mt-2">Studios are limited — each space is intentionally one of one.</p>
            </div>
            <div className="animate-fade-in stagger-2">
              <div className="rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.08)]">
                <img src={HERO_IMG} alt="Minimal studio interior" className="w-full h-72 sm:h-96 object-cover" loading="eager" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* A space for your specialism */}
      <SpecialismSection />

      {/* Main shopfront banner */}
      <ShopfrontBanner />

      {/* Shop vs Studio comparison with viewing invitation */}
      <ComparisonSection />

      {/* Why us */}
      <section className="py-16 sm:py-24 bg-white" data-testid="why-us-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A] tracking-tight mb-12 text-center">
            Built for how you actually work
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Your own private space', desc: 'A dedicated studio that\'s entirely yours. No sharing, no compromises.' },
              { title: 'One monthly fee', desc: 'Your studio and included services in one simple monthly payment.' },
              { title: 'Ready when you are', desc: 'A professional space prepared for you to start seeing clients.' },
              { title: 'Support when you need it', desc: 'Our team is here to help with setup, maintenance and anything in between.' },
            ].map((item, i) => (
              <div key={i} className={`bg-white rounded-2xl p-6 sm:p-8 border border-[#E5E2DC] animate-fade-in stagger-${i + 1}`}>
                <h3 className="font-heading text-base font-medium text-[#1A1A1A] mb-2">{item.title}</h3>
                <p className="text-sm text-[#5C5C55] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#8A8B85] text-center mt-6">Exclusions and separately charged services vary by location and are explained upfront.</p>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 sm:py-24" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A] tracking-tight mb-3 text-center">
            Simple from start to finish
          </h2>
          <p className="text-sm text-[#5C5C55] text-center mb-12">From first enquiry to getting started — a simple, guided process.</p>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-center gap-4 sm:gap-6">
            {steps.map((step, i) => (
              <React.Fragment key={step.num}>
                {i > 0 && <div className="hidden sm:block w-12 h-px bg-[#E5E2DC]" />}
                <div className={`flex items-center gap-3 bg-white rounded-2xl border border-[#E5E2DC] px-6 py-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)] animate-fade-in stagger-${i + 1}`}>
                  <div className="w-9 h-9 rounded-xl bg-[#E8EDE5] flex items-center justify-center">
                    <step.icon className="w-4 h-4 text-[#3A4035]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-[10px] text-[#8A8B85] uppercase tracking-wider font-semibold">Step {step.num}</p>
                    <p className="text-sm font-medium text-[#1A1A1A]">{step.title}</p>
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Banner */}
      <PractitionerBanner />

      {/* Locations preview */}
      <section className="bg-white py-16 sm:py-24" data-testid="locations-preview-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A] tracking-tight mb-4">
            Studios in the neighbourhoods you know
          </h2>
          <p className="text-base text-[#5C5C55] mb-8 max-w-xl mx-auto">
            We're building a network of local studios — designed to support independent professionals where they already work and live.
          </p>
          <Link to="/locations" className="inline-flex items-center gap-2 bg-[#3A4035] text-white rounded-full px-7 py-3.5 text-sm font-medium hover:bg-[#2B3027] transition-colors" data-testid="locations-preview-cta">
            View locations <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
      </section>

      {/* Brand statement */}
      <section className="py-20 sm:py-28" data-testid="brand-statement">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 text-center">
          <p className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-[#1A1A1A] tracking-tight leading-tight">
            One neighbourhood.<br />One business.<br />One studio at a time.
          </p>
          <p className="text-base text-[#5C5C55] mt-6 max-w-md mx-auto">
            Building a network of local studios that support independent professionals.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-24" data-testid="cta-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="bg-[#3A4035] rounded-3xl p-8 sm:p-12 lg:p-16 text-center relative overflow-hidden">
            <h2 className="font-heading text-2xl sm:text-3xl font-medium text-white tracking-tight mb-4 relative z-10">
              Find your studio
            </h2>
            <p className="text-base text-white/70 mb-2 max-w-lg mx-auto relative z-10">
              Whether you're starting out or growing your client base, we'll help you get set up quickly and properly.
            </p>
            <p className="text-sm text-white/50 mb-8 relative z-10">View a studio. See how it works. Decide with confidence.</p>
            <Link to="/book-a-viewing" className="inline-flex items-center gap-2 bg-white text-[#3A4035] rounded-full px-7 py-3.5 text-sm font-medium hover:bg-[#F2F0EB] transition-colors relative z-10" data-testid="cta-viewing">
              Book a viewing <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <p className="text-sm text-white/40 mt-4 relative z-10">No pressure. No obligation. Just come and see.</p>
            <p className="text-sm text-white/50 mt-2 relative z-10">Studios are limited — each space is intentionally one of one.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
